import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { ReportIssueInputs } from '../../models/reportIssue.model';
import { ReportIssueService } from '../../services/report-issue.service';
import { SnapshotService } from 'src/app/modules/snapshot/Services/snapshot.service';
import { AlertMessageService } from '../../services/alert-message.service';


@Component({
  selector: 'app-report-issue-popup',
  templateUrl: './report-issue-popup.component.html',
  styleUrls: ['./report-issue-popup.component.css']
})
export class ReportIssuePopupComponent implements OnInit, OnChanges {


  headerTitle: string = "Report An Issue";
  report: ReportIssueInputs = {};
  files: any[] = []; // Array to store file details
  uploadSizeExceed: boolean = false;
  // When viewing an existing incident in read-only mode
  @Input() readOnlyMode: boolean = false;
  // Existing incident object passed from parent (for view mode)
  @Input() incident: any;
  // Internal flag to show selection summary block only if data exists
  showSelectionSummary: boolean = false;


  @Input() ReportInput!: ReportIssueInputs;
  @Input() SelectedModule: string = '';
  @Input() SelectionSummary: string = '';
  @Input() ClientName: string = '';
  @Input() StudyName: string = '';
  @Input() SnapshotName: string = '';
  // When true (landing page usage) hide selection summary, file upload & record controls
  @Input() landingMinimal: boolean = false;
  @Output() closePopup: EventEmitter<boolean> = new EventEmitter();
  @Output() downloadAllAttachments: EventEmitter<any[]> = new EventEmitter();
  @Output() downloadReportExcel: EventEmitter<any> = new EventEmitter();

  constructor(private reportService: ReportIssueService, public _configService: SnapshotService, private alertService: AlertMessageService ) {
    // Initialize report object
    this.report = {
      title: '',
      description: '',
      selectionSummary: '',
      files: []
    };
  }

  ngOnInit() {
    // Set client study information from inputs first, then fall back to service data
    this.setClientStudyInformation();

    // For read-only mode, don't set selection summary from service - it will be set from incident data
    if (this.readOnlyMode) {
      // In read-only mode, if incident is already available, map it immediately
      if (this.incident) {
        this.mapIncidentToReport();
      } else {
        // In read-only mode, wait for incident data to be mapped
        this.report.selectionSummary = '';
        this.showSelectionSummary = false;
      }
      return;
    }

    // Handle selection summary based on module and landing minimal mode (only for non-read-only mode)
    if (!this.landingMinimal) {
      if (this.SelectedModule == 'CROSSTAB' || this.SelectedModule == 'BRAND ANALYSIS') {
        this.report.selectionSummary = this.SelectionSummary;
      } else {
        if (this._configService && this._configService.selectionSummaryTextTitle) {
          this.report.selectionSummary = this._configService.selectionSummaryTextTitle;
        } else {
          this.report.selectionSummary = '';
        }
      }
      // Show selection summary if it has a value
      this.showSelectionSummary = !!this.report.selectionSummary;
    } else {
      // For landing minimal mode, clear selection summary and hide field
      this.report.selectionSummary = '';
      this.showSelectionSummary = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If incident changes while in read-only mode, map its fields
    if (this.readOnlyMode && changes['incident'] && this.incident) {
      this.mapIncidentToReport();
    }
  }

  private mapIncidentToReport(): void {
    if (!this.incident) return;
    
    // Override header with Incident No. formatted like 0001
    if (this.incident.issueNo !== undefined && this.incident.issueNo !== null) {
     this.headerTitle = `Issue No: ${this.incident.issueNo}`;

    }
    // Map incident data to report-like structure for template reuse
    this.report.title = this.incident.reportTitle || '';
    this.report.description = this.incident.experience || '';
    
    // In read-only mode, use incident-specific selection summary
    // Handle null explicitly and convert to empty string for display
    this.report.selectionSummary = this.incident.selectionSummary === null ? '' : (this.incident.selectionSummary || '');
    this.showSelectionSummary = !!this.report.selectionSummary && !this.landingMinimal;
    
    // Attachments list (assumes incident.attachments is an array of filenames or objects)
    if (Array.isArray(this.incident.attachments)) {
      this.files = this.incident.attachments.map((f: any) => {
        // Normalize to have name property
        return typeof f === 'string' ? { name: f } : f;
      });
    } else {
      this.files = [];
    }
  }

  private setClientStudyInformation(): void {
    // First, try to use input properties
    if (this.ClientName || this.StudyName || this.SnapshotName) {
      this.report.client_name = this.ClientName || '';
      this.report.study_name = this.StudyName || '';
      this.report.snapshot_name = this.SnapshotName || '';
      this.report.module_name = this.SelectedModule || '';
    } 
    // Fall back to service data if available
    else if (this.reportService && this.reportService.clientStudy) {
      let clientStudy = this.reportService.clientStudy;
      this.report.client_name = clientStudy.clientName || '';
      this.report.study_name = clientStudy.StudyName || '';
      this.report.snapshot_name = clientStudy.SubStudy || '';
      this.report.module_name = clientStudy.ModuleName || this.SelectedModule || '';
    } 
    // Final fallback: try to get from snapshot service if available
    else if (this._configService) {
      this.report.client_name = this._configService.clientName || '';
      this.report.study_name = this._configService.selectedStudy || '';
      this.report.snapshot_name = this._configService.snapshotSelectedName || '';
      this.report.module_name = this.SelectedModule || 'SNAPSHOT';
    }
    // Last resort: set defaults
    else {
      this.report.client_name = '';
      this.report.study_name = '';
      this.report.snapshot_name = '';
      this.report.module_name = this.SelectedModule || '';
    }
  }

  close(): void {
    this.closePopup.emit();
  }

  onFileSelected(event: any): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const selectedFiles = Array.from(input.files);

      // Initialize report.files if it's null/undefined
      this.report.files = this.report.files || [];

      // Calculate the total size of currently selected and previously added files
      let totalSize = (this.report.files || []).reduce((sum, file) => sum + file.size, 0);

      selectedFiles.forEach((file) => {
        if (totalSize + file.size <= 10 * 1024 * 1024) {
          totalSize += file.size;
        } else {
          this.uploadSizeExceed = true;
          return;
        }
      });

      if (!this.uploadSizeExceed) {
        // Check if there are already files and append new files
        if (this.report.files && this.report.files.length > 0) {
          this.report.files = this.report.files.concat(selectedFiles);
        } else {
          this.report.files = selectedFiles;
        }
        this.files = this.report.files;
      }
    }
  }




  // Submit Report
  submitReport() {
    if (this.readOnlyMode) {
      // In read-only mode, submission is disabled
      return;
    }
    // For landing minimal mode, ensure no file-related properties cause issues
    if (this.landingMinimal) {
      // Clear any file-related properties for minimal mode
      this.report.files = [];
      this.files = [];
    }

    // Ensure required fields are set
    if (!this.report.title || this.report.title.trim() === '') {
      this.alertService.showAlert({ show: true, header: 'Validation', message: 'Please enter a title for your report.' });
      return;
    }

    
    if (Array.isArray(this.report.files)) {
      this.report.files = this.report.files.filter(f => f instanceof File);
    } 
    else {
      this.report.files = [];
    }



    // Debug: Log the report data to verify client study information is included

    if (this.reportService && this.reportService.saveReportIssue) {
      this.reportService.saveReportIssue(this.report).subscribe({
        next: () => {
          this.alertService.showAlert({ show: true, header: 'Success', message: 'Report submitted successfully!' });
          this.close();
        },
        error: (error) => {
          console.error('Error submitting report:', error);
          this.alertService.showAlert({ show: true, header: 'Error', message: 'Error submitting report. Please try again.' });
        }
      });
    } else {
      this.alertService.showAlert({ show: true, header: 'Error', message: 'Report service is not available. Please try again later.' });
    }
  }



  removeFile(fileToRemove: { name: string; size: number }): void {
    this.files = this.files.filter(file => file !== fileToRemove);
    this.report.files = this.report.files?.filter(file => file.name !== fileToRemove.name);
  }

  // Emit all current attachment objects for parent to handle batch download
  onDownloadAllAttachments(): void {
    if (this.readOnlyMode && this.files.length > 0) {
      this.downloadAllAttachments.emit(this.files);
    }
  }

  // Emit event for excel export (parent decides how to generate)
  onDownloadReportExcel(): void {
    if (this.readOnlyMode) {
      this.downloadReportExcel.emit({
        issueNo: this.incident?.issueNo,
        title: this.report.title,
        description: this.report.description,
        selectionSummary: this.report.selectionSummary
      });
    }
  }
}
